import { inject, Injectable, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { environment } from '@environments/environment';
import { MailtasticAuthorizationDetails } from '@model/enums/mailtastic-authorization-details.enum';
import { AccountService } from '@services/account/account.service';
import { AlertService } from '@services/alert/alert.service';
import { AdblockService } from '@services/adblock/adblock.service';
import { AuthService } from '@services/auth/auth.service';
import { IExternalAdminGetAccounts } from '@services/external-admin/external-admin.interface';
import { ExternalAdminService } from '@services/external-admin/external-admin.service';
import { PaymentService } from '@services/payment/payment.service';
import { UserService } from '@services/user/user.service';
import { BehaviorSubject, catchError, finalize, iif, Observable, of, switchMap, tap, withLatestFrom } from 'rxjs';
import { ModalService } from '@services/modal/modal.service';
import { Router } from '@angular/router';
import { IUserLoginSuccess } from '@services/user/user-service.interface';

@Injectable()
export class SimpleAuthService {
  private router = inject(Router);

  isCognismSignatures = environment.isCogSig;

  /**
   * Login btn disabled
   */
  private _loginBtnDisabled$ = new BehaviorSubject<boolean>(false);
  loginBtnDisabled$ = this._loginBtnDisabled$.asObservable();

  /**
   * Used to display error message
   */
  errorMessage = signal<string>('');

  constructor(
    private accountService: AccountService,
    private adblockService: AdblockService,
    private alert: AlertService,
    private authService: AuthService,
    private externalAdminService: ExternalAdminService,
    private modal: ModalService,
    private paymentService: PaymentService,
    private userService: UserService
  ) {}

  /**
   * Login to the application using a mailtastic account
   * @param email - Email of account
   * @param password - Password of account
   * @param isSuperAdmin - The super admin trigger
   * @returns Array of external admin list
   */
  login(email: string, password: string, isSuperAdmin = false): Observable<IExternalAdminGetAccounts[]> {
    this.enableDisableLoginBtn(true);
    return this.userService.login(email, password, isSuperAdmin).pipe(
      switchMap(result => this.completeLogin(result, isSuperAdmin)),
      catchError((err: HttpErrorResponse) => {
        let errorMessage = '';
        if (err.message === 'Authentication failed. Login incorrect' || err.message === 'Authentication failed.') {
          (errorMessage = this.alert.translate('wrong_pw_sl')), this.alert.translate('wrong_pw_hl');
        } else if (err.message === 'RETRY_AFTER_ONE_HOUR') {
          errorMessage = this.alert.translate('confirmationmail_error_note');
        } else if (err.message === 'TRY_TO_LOGIN_WITH_SSO') {
          errorMessage = this.alert.translate('TRY_TO_LOGIN_WITH_SSO');
        } else if (err.message === 'login_problem_occured') {
          errorMessage = this.alert.translate('login_problem_occured');
        } else if (err.message === 'wrong_password_5_or_more_times') {
          this.modal.openFailedLoginModal().subscribe(() => void this.router.navigate(['forgotpassword']));
        } else {
          errorMessage = err.message;
        }
        this.errorMessage.set(errorMessage);
        this.enableDisableLoginBtn(false);
        throw err;
      }),
      finalize(() => this.enableDisableLoginBtn(false))
    );
  }

  /**
   * Executes shared post-login flow for OTP and credential based logins
   * @param result - Successful login payload
   * @param isSuperAdmin - Indicates whether super admin login bypass should be applied
   * @returns Observable emitting external admin accounts
   */
  public completeLogin(result: IUserLoginSuccess, isSuperAdmin = false): Observable<IExternalAdminGetAccounts[]> {
    return of(result).pipe(
      tap(login => this.persistLogin(login)),
      withLatestFrom(this.authService.authorizationScope$),
      switchMap(([_, authorization]) =>
        iif(
          () => this.isCognismSignatures,
          of(null),
          this.paymentService.getCustomerSubscriptionData(authorization.accountId)
        )
      ),
      switchMap(() => iif(() => isSuperAdmin, of([]), this.externalAdminService.getAccounts()))
    );
  }

  /**
   * Trigger login button enable disable
   * @param isDisbled - boolean
   */
  private enableDisableLoginBtn(isDisbled: boolean): void {
    this._loginBtnDisabled$.next(isDisbled);
  }

  /**
   * Stores login related data and side effects shared across login flows
   * @param result - Successful login payload
   */
  private persistLogin(result: IUserLoginSuccess): void {
    this.authService.setKeyInLocalstorage(MailtasticAuthorizationDetails.userId, result?.userId || '');
    this.authService.setKeyInLocalstorage(MailtasticAuthorizationDetails.signiticAlert, (result?.isSigniticAlertShown || false).toString());
    this.authService.setKeyInLocalstorage(MailtasticAuthorizationDetails.defaultUserId, result?.userId || '');
    this.accountService.setUserAccountData(
      result.companyName,
      result.firstname,
      result.lastname,
      result.email,
      result.adminId,
      result.accountId,
      undefined,
      result.allowEditPersonalDataEUC
    );

    if (!result.isUserLoggedInFirstTime) {
      this.adblockService.openAdblockContentModalIfExist();
    }

    if (result.isSuperAdmin) {
      this.accountService.setSuperAdminAccountData(result);
      this.authService.updateCustomerLoginEmails([]);
    }

    this.authService.setToken(result.token);
  }
}
